import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { taskApi } from './task-api'
import type { TaskListParams, CreateTaskRequest, UpdateTaskRequest } from './task-types'
import { useAppDispatch, showLoader, hideLoader, showSnackbar } from '@/store'
import { getResponseMessage } from '@/utils'

const taskKeys = {
  all: () => ['tasks-clinician-portal'] as const,
  list: (params?: TaskListParams) => ['tasks-clinician-portal', 'list', params ?? {}] as const,
  detail: (taskId: string) => ['tasks-clinician-portal', 'detail', taskId] as const,
  assignedOpenCount: () => ['tasks-clinician-portal', 'assigned-open-count'] as const,
}

/** Minimal request — `assignedOpenCount` is filter-independent, so one row is enough. */
const ASSIGNED_OPEN_COUNT_PARAMS: TaskListParams = { assignedToMe: true, size: 1 }

/** Fallback poll so tasks assigned to me by *other* users surface without a reload. */
const ASSIGNED_OPEN_COUNT_POLL_MS = 60_000

/*** GET /api/master/tasks */
export const useTasks = (params?: TaskListParams) =>
  useQuery({
    queryKey: taskKeys.list(params),
    queryFn: () => taskApi.getAll(params),
    staleTime: 0,
  })

/**
 * Logged-in user's open assigned-task count for the nav badge.
 * Lives under `taskKeys.all()` so every create / update / archive / restore
 * mutation below invalidates it alongside the list queries.
 */
export const useAssignedOpenTaskCount = (enabled = true) =>
  useQuery({
    queryKey: taskKeys.assignedOpenCount(),
    queryFn: () => taskApi.getAll(ASSIGNED_OPEN_COUNT_PARAMS),
    select: (data) => data?.assignedOpenCount ?? 0,
    enabled,
    staleTime: 0,
    refetchInterval: ASSIGNED_OPEN_COUNT_POLL_MS,
    refetchOnWindowFocus: true,
  })

/*** GET /api/master/tasks/{taskId} */
export const useTaskByUuid = (taskId: string) =>
  useQuery({
    queryKey: taskKeys.detail(taskId),
    queryFn: () => taskApi.getById(taskId),
    enabled: Boolean(taskId),
  })

/*** POST /api/master/tasks */
export const useCreateTask = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  return useMutation({
    mutationFn: (payload: CreateTaskRequest) => taskApi.create(payload),
    onMutate: () => {
      dispatch(showLoader())
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all() })
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'success',
          message: getResponseMessage(response, 'Task created successfully!'),
        }),
      )
    },
    onError: (error: unknown) => {
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'error',
          message: getResponseMessage(error, 'Failed to create task'),
        }),
      )
    },
  })
}

/*** PUT /api/master/tasks/{taskId} */
export const useUpdateTask = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  return useMutation({
    mutationFn: ({ taskId, payload }: { taskId: string; payload: UpdateTaskRequest }) =>
      taskApi.update(taskId, payload),
    onMutate: () => {
      dispatch(showLoader())
    },
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all() })
      queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.taskId) })
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'success',
          message: getResponseMessage(response, 'Task updated successfully!'),
        }),
      )
    },
    onError: (error: unknown) => {
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'error',
          message: getResponseMessage(error, 'Failed to update task'),
        }),
      )
    },
  })
}

/*** PUT /api/master/tasks/{taskId}/complete */
export const useCompleteTask = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  return useMutation({
    mutationFn: (taskId: string) => taskApi.complete(taskId),
    onMutate: () => {
      dispatch(showLoader())
    },
    onSuccess: (response, taskId) => {
      // Completing drops the task from the nav badge count — `taskKeys.all()` covers it.
      queryClient.invalidateQueries({ queryKey: taskKeys.all() })
      queryClient.invalidateQueries({ queryKey: taskKeys.detail(taskId) })
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'success',
          message: getResponseMessage(response, 'Task marked as complete!'),
        }),
      )
    },
    onError: (error: unknown) => {
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'error',
          message: getResponseMessage(error, 'Failed to complete task'),
        }),
      )
    },
  })
}

/*** PATCH /api/master/tasks/{taskId}/archive */
export const useArchiveTask = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  return useMutation({
    mutationFn: (taskId: string) => taskApi.archive(taskId),
    onMutate: () => {
      dispatch(showLoader())
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all() })
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'success',
          message: getResponseMessage(response, 'Task archived successfully!'),
        }),
      )
    },
    onError: (error: unknown) => {
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'error',
          message: getResponseMessage(error, 'Failed to archive task'),
        }),
      )
    },
  })
}

/*** PATCH /api/master/tasks/{taskId}/restore */
export const useRestoreTask = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  return useMutation({
    mutationFn: (taskId: string) => taskApi.restore(taskId),
    onMutate: () => {
      dispatch(showLoader())
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all() })
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'success',
          message: getResponseMessage(response, 'Task restored successfully!'),
        }),
      )
    },
    onError: (error: unknown) => {
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'error',
          message: getResponseMessage(error, 'Failed to restore task'),
        }),
      )
    },
  })
}
